/**
 * Label Storage Utility
 * Manages location labels using localStorage for Phase 1 MVP
 *
 * Label Structure:
 * {
 *   locationId: number,
 *   labels: [
 *     { id: string, name: string, color: string, createdAt: timestamp }
 *   ]
 * }
 */

const STORAGE_KEY = 'caro_location_labels';
const RECENT_SEARCHES_KEY = 'caro_location_search_history';
const PRESET_LABELS_KEY = 'caro_preset_labels';

// Default preset labels available to all users
const DEFAULT_PRESET_LABELS = [
  { id: 'maintenance', name: 'Maintenance', color: '#ff9800' },
  { id: 'active', name: 'Active', color: '#4caf50' },
  { id: 'inactive', name: 'Inactive', color: '#9e9e9e' },
  { id: 'new', name: 'New', color: '#2196f3' },
  { id: 'important', name: 'Important', color: '#f44336' },
  { id: 'archived', name: 'Archived', color: '#607d8b' },
  { id: 'monitored', name: 'Monitored', color: '#9c27b0' },
  { id: 'restricted', name: 'Restricted Access', color: '#e91e63' },
];

class LabelStorage {
  /**
   * Get all labels for all locations
   * @returns {Object} Map of locationId -> labels array
   */
  static getAllLabels() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('[LabelStorage] Error reading labels:', error);
      return {};
    }
  }

  /**
   * Get labels for a specific location
   * @param {number|string} locationId - The location ID
   * @returns {Array} Array of label objects
   */
  static getLabelsForLocation(locationId) {
    const allLabels = this.getAllLabels();
    return allLabels[locationId] || [];
  }

  /**
   * Add a label to a location
   * @param {number|string} locationId - The location ID
   * @param {Object} label - Label object { name, color }
   * @returns {Object} The created label with id and timestamp
   */
  static addLabel(locationId, label) {
    const allLabels = this.getAllLabels();
    const locationLabels = allLabels[locationId] || [];

    // Check for duplicate label name
    const exists = locationLabels.some(l => l.name.toLowerCase() === label.name.toLowerCase());
    if (exists) {
      throw new Error(`Label "${label.name}" already exists for this location`);
    }

    const newLabel = {
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: label.name,
      color: label.color || '#2196f3',
      createdAt: Date.now(),
    };

    allLabels[locationId] = [...locationLabels, newLabel];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allLabels));

    return newLabel;
  }

  /**
   * Add multiple labels to a location at once
   * @param {number|string} locationId - The location ID
   * @param {Array} labels - Array of label objects
   * @returns {Array} Array of created labels
   */
  static addMultipleLabels(locationId, labels) {
    return labels.map(label => this.addLabel(locationId, label));
  }

  /**
   * Remove a label from a location
   * @param {number|string} locationId - The location ID
   * @param {string} labelId - The label ID to remove
   */
  static removeLabel(locationId, labelId) {
    const allLabels = this.getAllLabels();
    const locationLabels = allLabels[locationId] || [];

    allLabels[locationId] = locationLabels.filter(l => l.id !== labelId);

    // Clean up empty location entries
    if (allLabels[locationId].length === 0) {
      delete allLabels[locationId];
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(allLabels));
  }

  /**
   * Update a label
   * @param {number|string} locationId - The location ID
   * @param {string} labelId - The label ID to update
   * @param {Object} updates - Updates to apply { name?, color? }
   */
  static updateLabel(locationId, labelId, updates) {
    const allLabels = this.getAllLabels();
    const locationLabels = allLabels[locationId] || [];

    const labelIndex = locationLabels.findIndex(l => l.id === labelId);
    if (labelIndex === -1) {
      throw new Error('Label not found');
    }

    // Check for duplicate name if name is being changed
    if (updates.name) {
      const duplicate = locationLabels.some(
        (l, idx) => idx !== labelIndex && l.name.toLowerCase() === updates.name.toLowerCase()
      );
      if (duplicate) {
        throw new Error(`Label "${updates.name}" already exists for this location`);
      }
    }

    locationLabels[labelIndex] = {
      ...locationLabels[labelIndex],
      ...updates,
      updatedAt: Date.now(),
    };

    allLabels[locationId] = locationLabels;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allLabels));

    return locationLabels[labelIndex];
  }

  /**
   * Remove all labels from a location
   * @param {number|string} locationId - The location ID
   */
  static clearLocationLabels(locationId) {
    const allLabels = this.getAllLabels();
    delete allLabels[locationId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(allLabels));
  }

  /**
   * Search for locations by label
   * @param {string} labelName - The label name to search for
   * @returns {Array} Array of location IDs that have this label
   */
  static findLocationsByLabel(labelName) {
    const allLabels = this.getAllLabels();
    const locationIds = [];

    Object.keys(allLabels).forEach(locationId => {
      const hasLabel = allLabels[locationId].some(
        label => label.name.toLowerCase() === labelName.toLowerCase()
      );
      if (hasLabel) {
        locationIds.push(parseInt(locationId));
      }
    });

    return locationIds;
  }

  /**
   * Get all unique label names across all locations
   * @returns {Array} Array of unique label names with usage count
   */
  static getAllUniqueLabelNames() {
    const allLabels = this.getAllLabels();
    const labelMap = new Map();

    Object.values(allLabels).forEach(locationLabels => {
      locationLabels.forEach(label => {
        if (labelMap.has(label.name)) {
          labelMap.get(label.name).count++;
        } else {
          labelMap.set(label.name, {
            name: label.name,
            color: label.color,
            count: 1,
          });
        }
      });
    });

    return Array.from(labelMap.values()).sort((a, b) => b.count - a.count);
  }

  /**
   * Bulk apply label to multiple locations
   * @param {Array} locationIds - Array of location IDs
   * @param {Object} label - Label to apply
   */
  static bulkApplyLabel(locationIds, label) {
    locationIds.forEach(locationId => {
      try {
        this.addLabel(locationId, label);
      } catch (error) {
        // Skip if label already exists
        if (!error.message.includes('already exists')) {
          throw error;
        }
      }
    });
  }

  /**
   * Export all labels as JSON
   * @returns {string} JSON string of all labels
   */
  static exportLabels() {
    const allLabels = this.getAllLabels();
    return JSON.stringify(allLabels, null, 2);
  }

  /**
   * Import labels from JSON
   * @param {string} jsonString - JSON string of labels
   * @param {boolean} merge - Whether to merge with existing labels (default: false)
   */
  static importLabels(jsonString, merge = false) {
    try {
      const importedLabels = JSON.parse(jsonString);

      if (merge) {
        const existingLabels = this.getAllLabels();
        const mergedLabels = { ...existingLabels };

        Object.keys(importedLabels).forEach(locationId => {
          if (mergedLabels[locationId]) {
            // Merge labels, avoiding duplicates
            const existing = mergedLabels[locationId];
            const imported = importedLabels[locationId];

            imported.forEach(importedLabel => {
              const exists = existing.some(
                l => l.name.toLowerCase() === importedLabel.name.toLowerCase()
              );
              if (!exists) {
                existing.push(importedLabel);
              }
            });
          } else {
            mergedLabels[locationId] = importedLabels[locationId];
          }
        });

        localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedLabels));
      } else {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(importedLabels));
      }

      return true;
    } catch (error) {
      console.error('[LabelStorage] Error importing labels:', error);
      throw new Error('Invalid label data format');
    }
  }

  /**
   * Get preset labels
   * @returns {Array} Array of preset label definitions
   */
  static getPresetLabels() {
    try {
      const customPresets = localStorage.getItem(PRESET_LABELS_KEY);
      const presets = customPresets ? JSON.parse(customPresets) : [];
      return [...DEFAULT_PRESET_LABELS, ...presets];
    } catch (error) {
      console.error('[LabelStorage] Error reading preset labels:', error);
      return DEFAULT_PRESET_LABELS;
    }
  }

  /**
   * Add a custom preset label
   * @param {Object} preset - Preset label definition { name, color }
   */
  static addPresetLabel(preset) {
    try {
      const presets = this.getPresetLabels().filter(
        p => !DEFAULT_PRESET_LABELS.some(d => d.id === p.id)
      );

      const newPreset = {
        id: `custom_${Date.now()}`,
        name: preset.name,
        color: preset.color || '#2196f3',
      };

      presets.push(newPreset);
      localStorage.setItem(PRESET_LABELS_KEY, JSON.stringify(presets));

      return newPreset;
    } catch (error) {
      console.error('[LabelStorage] Error adding preset label:', error);
      throw error;
    }
  }

  // ===== SEARCH HISTORY =====

  /**
   * Get recent searches
   * @param {number} limit - Maximum number of recent searches to return
   * @returns {Array} Array of recent search terms
   */
  static getRecentSearches(limit = 10) {
    try {
      const data = localStorage.getItem(RECENT_SEARCHES_KEY);
      const searches = data ? JSON.parse(data) : [];
      return searches.slice(0, limit);
    } catch (error) {
      console.error('[LabelStorage] Error reading search history:', error);
      return [];
    }
  }

  /**
   * Add a search term to history
   * @param {string} searchTerm - The search term to save
   */
  static addToSearchHistory(searchTerm) {
    if (!searchTerm || searchTerm.trim().length === 0) return;

    try {
      let searches = this.getRecentSearches(50); // Keep last 50

      // Remove if already exists (to move to top)
      searches = searches.filter(s => s !== searchTerm);

      // Add to beginning
      searches.unshift(searchTerm);

      // Keep only last 50
      searches = searches.slice(0, 50);

      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
    } catch (error) {
      console.error('[LabelStorage] Error saving search history:', error);
    }
  }

  /**
   * Clear search history
   */
  static clearSearchHistory() {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  }

  /**
   * Clear all label data (for testing/debugging)
   */
  static clearAll() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RECENT_SEARCHES_KEY);
    localStorage.removeItem(PRESET_LABELS_KEY);
  }
}

export default LabelStorage;
